import { defineComponent as _defineComponent } from 'vue'
import { EntityTree } from '@/views/Automation/CampaignCreatorV2/ab/core/GroupABEntityCombiner';
import { NameType } from '@/views/Automation/CampaignCreatorV2/ab/core/helpers';
import CcAlert from '@/views/Automation/CampaignCreatorV2/components/CcAlert/CcAlert.ts.vue';
import CcCard from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCard.ts.vue';
import CcCardItem from '@/views/Automation/CampaignCreatorV2/components/CcCard/CcCardItem.ts.vue';
import CcCheckbox from '@/views/Automation/CampaignCreatorV2/components/CcCheckbox/CcCheckbox.ts.vue';
import CcDropdownLarge from '@/views/Automation/CampaignCreatorV2/components/CcDropdown/CcDropdownLarge.ts.vue';
import CcIcon from '@/views/Automation/CampaignCreatorV2/components/CcIcon/CcIcon.ts.vue';
import { useTour } from '@/views/Automation/CampaignCreatorV2/helpers/tourGuide';
import CcPreviewCard from '@/views/Automation/CampaignCreatorV2/preview/components/CcPreviewCard.ts.vue';
import CcPreviewCardList from '@/views/Automation/CampaignCreatorV2/preview/components/CcPreviewCardList.ts.vue';
import CcPreviewCardSmall from '@/views/Automation/CampaignCreatorV2/preview/components/CcPreviewCardSmall.ts.vue';
import useAbGroups from '@/views/Automation/CampaignCreatorV2/store/AbGroups';
import useCampaignStore from '@/views/Automation/CampaignCreatorV2/store/Campaign';
import { SupportedEntities } from '@/views/Automation/CampaignCreatorV2/store/Types';
import { CardLayer } from '@/views/Automation/CampaignCreatorV2/types';
import { AdDTO } from '@/views/Automation/CampaignCreatorV2/validation/AdDTO';
import { AdSetDTO } from '@/views/Automation/CampaignCreatorV2/validation/AdSetDTO';
import { CampaignDTO } from '@/views/Automation/CampaignCreatorV2/validation/CampaignDTO';
import { chunk } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ManageCampaign.ts',
  setup(__props) {

onMounted(() => {
  useTour('ManageVariations');
});

const currentCampaignIndex = ref<{ entityId: string; variationIndex: number }>({
  entityId: '',
  variationIndex: 0,
});

const currentSelectedAdsetIndex = ref<Record<string, number>>({});
const abGroupStore = useAbGroups();
const abGroupGetter = storeToRefs(abGroupStore);
const campaignGetters = storeToRefs(useCampaignStore());
const campaignSpreadEnabled = computed(() => {
  const spread = abGroupGetter.getSpreadFactor.value;
  return !!Object.values(spread).length;
});

const campaignTrees = computed(() => {
  const { registry, updatedAt, campaignIds } = campaignGetters.getRegistry.value;
  const tree = campaignIds.map((cmpId) => {
    const entityCombiner = registry.getEntityCombiner(cmpId);
    return entityCombiner?.getEntityTree() || [];
  });
  console.log(`Variation tree updated at ${updatedAt}`, tree);
  return tree;
});

const campaignOpen = computed(
  () =>
    campaignTrees.value?.find((group) => group.entityId === currentCampaignIndex?.value?.entityId)
      ?.abVariationGenerated?.[currentCampaignIndex.value.variationIndex]
);
const spreadAdSetFactor = computed(() => abGroupGetter.getSpreadFactor.value[SupportedEntities.adset]);
const spreadAdFactor = computed(() => abGroupGetter.getSpreadFactor.value[SupportedEntities.ad]);
function changeAdSetSpreadFactor(value: string) {
  abGroupStore.setSpreadFactor(SupportedEntities.adset, Number(value));
  useCampaignStore().updateAbGroups();
}

function changeAdSpreadFactor(value: string) {
  abGroupStore.setSpreadFactor(SupportedEntities.ad, Number(value));
  useCampaignStore().updateAbGroups();
}

const campaignTasks = computed(() => {
  const { campaignTaskForCreation, updatedAt } = campaignGetters.getComputedCampaignForCreation.value;
  console.log('Campaign that will be created', { campaignTaskForCreation, updatedAt });
  // const taskMetrics = campaignTaskForCreation?.map((t) => t.getAllMetrics()).flat() ?? [];
  // const reducedMetrics = CampaignDTO.reduceMetrics(taskMetrics);

  const CalculatedCampaigns = [];
  for (const campaign of campaignTaskForCreation) {
    const adsets = campaign.getSubTaskList();
    const calculatedAdSets = [];
    for (const adset of adsets) {
      const ads = adset.getSubTaskList();
      const chunkedAdsBySpreadFactor = chunk(ads, spreadAdFactor.value || 999);
      calculatedAdSets.push(...chunkedAdsBySpreadFactor);
    }
    const chunkedAdset = chunk(calculatedAdSets, spreadAdSetFactor.value || 999);
    CalculatedCampaigns.push(...chunkedAdset);
  }
  return `A total of <span>${CalculatedCampaigns.length}</span> Campaigns, <span>${
    CalculatedCampaigns.flat(1).length
  }</span> AdSets and <span>${CalculatedCampaigns.flat(2).length}</span> Ads will be created`;
});

onMounted(() => {
  currentCampaignIndex.value = { entityId: campaignTrees?.value?.[0]?.entityId, variationIndex: 0 };
});

function shouldDisableStatusToggle(variations: EntityTree['abVariationGenerated']) {
  return (
    variations
      ?.map((c) => c.entityVariations)
      .flat()
      .map((v) => v.status)
      ?.filter(Boolean)?.length === 1
  );
}

function changeStatus(status: { status: boolean }, movingVariables: string[]) {
  useAbGroups().pauseAbVariantGlobal(status.status, movingVariables);
  useCampaignStore().updateAbGroups();
}

function selectCampaign(campaignGroupIndex: string, id: number) {
  currentCampaignIndex.value = { entityId: campaignGroupIndex, variationIndex: id };
}

function selectAdset(adset: { entityId: string; variationIndex: number }) {
  currentSelectedAdsetIndex.value = { ...currentSelectedAdsetIndex.value, [adset.entityId]: adset.variationIndex };
}

function duplicateGroup(campaignId: string) {
  useCampaignStore().cloneCampaign(campaignId);
  useCampaignStore().updateAbGroups();
}

function getName(entity: EntityTree) {
  const variables = entity.abVariationGenerated?.[0]?.entityVariations?.[0]?.variables || [];
  const nameVariable = variables.find((v) => v.field === 'name')?.value as NameType;
  return nameVariable?.name || '';
}

function currentAdsetVariaton(entityId: string) {
  const adset = campaignOpen?.value?.children?.find((adset) => adset.entityId === entityId);
  const adsetVariaton = (adset?.abVariationGenerated || [])[currentSelectedAdsetIndex.value?.[entityId] || 0];
  return adsetVariaton.entityVariations?.[0];
}

function currentAdset(entityId: string) {
  const adset = campaignOpen?.value?.children?.find((adset) => adset.entityId === entityId);
  const adsetVariaton = (adset?.abVariationGenerated || [])[currentSelectedAdsetIndex.value?.[entityId] || 0];
  return adsetVariaton;
}

function toggleCampaignWithValidation(value: boolean) {
  if (value) {
    changeAdSpreadFactor('1');
    changeAdSetSpreadFactor('1');
  } else {
    abGroupStore.resetSpreadFactor();
  }
}

return { __sfc: true,currentCampaignIndex, currentSelectedAdsetIndex, abGroupStore, abGroupGetter, campaignGetters, campaignSpreadEnabled, campaignTrees, campaignOpen, spreadAdSetFactor, spreadAdFactor, changeAdSetSpreadFactor, changeAdSpreadFactor, campaignTasks, shouldDisableStatusToggle, changeStatus, selectCampaign, selectAdset, duplicateGroup, getName, currentAdsetVariaton, currentAdset, toggleCampaignWithValidation, CcAlert, CcCard, CcCardItem, CcCheckbox, CcDropdownLarge, CcIcon, CcPreviewCard, CcPreviewCardList, CcPreviewCardSmall, CardLayer, AdDTO, AdSetDTO, CampaignDTO }
}

})